import {
  addStripSlide,
  buildStripArtboard,
  getContinuousSlideWidth,
  isStripWithinLimits,
  removeStripSlide,
  shiftSlideGuides,
} from "./continuousStrip.js";

export const getStripSlideSize = ({ artboard, slideCount } = {}) => {
  const slideWidth = getContinuousSlideWidth({
    width: artboard?.width,
    slideCount,
  });
  if (!slideWidth) return null;
  return { width: slideWidth, height: Number(artboard.height) };
};

export const stripToSlideArtboards = ({ artboards = {}, slideCount } = {}) =>
  Object.fromEntries(
    Object.entries(artboards).map(([artboardID, artboard]) => {
      const size = getStripSlideSize({ artboard, slideCount });
      return [artboardID, size ? { ...artboard, ...size } : artboard];
    })
  );

// A strip background always fills the whole strip, so a leftover crop (e.g.
// from clipping the layer while slides were removed) must not survive.
export const clearStripBackgroundCrop = ({ artboards = {} } = {}) => {
  let changed = false;
  const next = Object.fromEntries(
    Object.entries(artboards).map(([artboardID, artboard]) => {
      const nodes = artboard?.nodes || {};
      let artboardChanged = false;
      const nextNodes = Object.fromEntries(
        Object.entries(nodes).map(([nodeID, node]) => {
          if (!node?.isBackground || node.crop == null) return [nodeID, node];
          artboardChanged = true;
          const rest = { ...node };
          delete rest.crop;
          return [nodeID, rest];
        })
      );
      if (!artboardChanged) return [artboardID, artboard];
      changed = true;
      return [artboardID, { ...artboard, nodes: nextNodes }];
    })
  );
  return changed ? next : artboards;
};

export const formatStripSlideLabel = ({ artboard, slideCount } = {}) => {
  const size = getStripSlideSize({ artboard, slideCount });
  if (!size) return "";
  return `${size.width}×${size.height}`;
};

export const buildContinuousPresetArtboard = ({ preset, slideCount } = {}) => {
  if (!preset) return null;
  const artboard = buildStripArtboard({
    slideWidth: preset.width,
    slideHeight: preset.height,
    slideCount,
    label: preset.label,
  });
  return isStripWithinLimits(artboard) ? artboard : null;
};

export const isContinuousPresetAllowed = ({ preset, slideCount } = {}) =>
  Boolean(buildContinuousPresetArtboard({ preset, slideCount }));

export const hasContentInSlideZone = ({
  nodes = {},
  nodeLayouts = {},
  slideIndex,
  slideWidth,
} = {}) => {
  const zoneStart = slideIndex * slideWidth;
  const zoneEnd = zoneStart + slideWidth;
  return Object.entries(nodes).some(([nodeID, node]) => {
    const layout = nodeLayouts[nodeID];
    if (!layout || node?.isBackground) return false;
    return layout.x < zoneEnd && layout.x + layout.width > zoneStart;
  });
};

export const hasContentInArtboardsSlideZone = ({
  artboards = {},
  slideIndex,
  slideCount,
} = {}) =>
  Object.values(artboards).some((artboard) => {
    const size = getStripSlideSize({ artboard, slideCount });
    if (!size) return false;
    return hasContentInSlideZone({
      nodes: artboard.nodes,
      nodeLayouts: artboard.nodeLayouts,
      slideIndex,
      slideWidth: size.width,
    });
  });

const applyToArtboards = ({ artboards, slideCount, apply }) => {
  const next = {};
  let nextCount = slideCount;
  let changed = true;
  Object.entries(artboards).forEach(([artboardID, artboard]) => {
    const size = getStripSlideSize({ artboard, slideCount });
    if (!size) {
      changed = false;
      return;
    }
    const result = apply({ artboard, size });
    if (!result.changed) changed = false;
    nextCount = result.slideCount;
    next[artboardID] = {
      ...artboard,
      width: result.width,
      nodes: result.nodes,
      nodeLayouts: result.nodeLayouts,
    };
  });
  if (!changed) return { changed: false, slideCount, artboards };
  return { changed: true, slideCount: nextCount, artboards: next };
};

export const addSlideToArtboards = ({ artboards = {}, slideCount } = {}) =>
  applyToArtboards({
    artboards,
    slideCount,
    apply: ({ artboard, size }) =>
      addStripSlide({
        nodes: artboard.nodes,
        nodeLayouts: artboard.nodeLayouts,
        slideCount,
        slideWidth: size.width,
        height: size.height,
      }),
  });

export const removeSlideFromArtboards = ({
  artboards = {},
  slideCount,
  slideIndex,
} = {}) => {
  const result = applyToArtboards({
    artboards,
    slideCount,
    apply: ({ artboard, size }) =>
      removeStripSlide({
        nodes: artboard.nodes,
        nodeLayouts: artboard.nodeLayouts,
        slideIndex,
        slideCount,
        slideWidth: size.width,
        height: size.height,
      }),
  });
  if (!result.changed) return result;
  const nextArtboards = {};
  Object.entries(result.artboards).forEach(([artboardID, artboard]) => {
    const slideGuides = shiftSlideGuides({
      slideGuides: artboards[artboardID]?.slideGuides,
      removedIndex: slideIndex,
      slideCount,
    });
    const next = { ...artboard };
    if (Object.keys(slideGuides).length) next.slideGuides = slideGuides;
    else delete next.slideGuides;
    nextArtboards[artboardID] = next;
  });
  return { ...result, artboards: nextArtboards };
};
